import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";
import bgTemple from "../assets/bg-temple.png";

function ConversationsList() {
  const [conversations, setConversations] = useState([]);
  const [isLongPress, setIsLongPress] = useState(false);
  const [pressedId, setPressedId] = useState(null);

  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const res = await API.get("/conversations");
      setConversations(res.data);
    } catch (err) {
      console.error("Erreur conversations:", err);
    }
  };

  const handleNewConversation = () => {
    navigate("/new-conversation");
  };

  const handleCreateGroup = () => {
    navigate("/new-conversation?mode=group");
  };

  const handleDelete = async (conv) => {
    if (conv.type === "public") return;

    const label =
      conv.type === "group"
        ? "Quitter ce groupe ?"
        : "Supprimer cette conversation ?";

    if (!window.confirm(label)) return;

    try {
      await API.delete(`/conversations/${conv.id}`);
      setConversations((prev) => prev.filter((c) => c.id !== conv.id));
    } catch (err) {
      console.error("Erreur suppression conversation:", err);
    }
  };

  const handlePressStart = (conv) => {
    if (conv.type === "public") return;

    setIsLongPress(false);
    setPressedId(conv.id);

    timerRef.current = setTimeout(() => {
      setIsLongPress(true);
      handleDelete(conv);
    }, 650);
  };

  const cancelPress = () => {
    clearTimeout(timerRef.current);
    setPressedId(null);
  };

  const openConversation = (conv) => {
    if (isLongPress) return;
    navigate(`/chat/${conv.id}`);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>Messagerie</p>
          <h1 style={styles.title}>Conversations</h1>
          <p style={styles.subtitle}>
            {conversations.length} conversation
            {conversations.length > 1 ? "s" : ""}
          </p>
        </div>

        <button style={styles.addBtn} onClick={handleNewConversation}>
          +
        </button>
      </div>

      <div style={styles.actionsRow}>
        <button style={styles.actionButton} onClick={handleNewConversation}>
          💬 Message privé
        </button>

        <button style={styles.actionButton} onClick={handleCreateGroup}>
          👥 Groupe
        </button>
      </div>

      <div style={styles.infoCard}>
        <span style={styles.infoIcon}>🏛️</span>
        <div>
          <strong>Salon de la loge</strong>
          <p>Le salon public est visible par tous les membres et ne peut pas être supprimé.</p>
        </div>
      </div>

      <div style={styles.list}>
        {conversations.length === 0 ? (
          <div style={styles.emptyCard}>
            <div style={styles.emptyIcon}>✉️</div>
            <h2>Aucune conversation</h2>
            <p>Commence une discussion privée ou crée un groupe.</p>
            <button style={styles.emptyButton} onClick={handleNewConversation}>
              Nouvelle conversation
            </button>
          </div>
        ) : (
          conversations.map((conv) => {
            const unread = Number(conv.unread_count || 0) > 0;
            const isGroup = conv.type === "group";
            const isPublic = conv.type === "public";
            const title = getConversationTitle(conv);
            const initials = getInitials(conv);

            return (
              <div
                key={conv.id}
                style={{
                  ...styles.card,
                  ...(isPublic ? styles.publicCard : {}),
                  ...(pressedId === conv.id ? styles.cardPressed : {}),
                  ...(unread ? styles.cardUnread : {}),
                }}
                onMouseDown={() => handlePressStart(conv)}
                onMouseUp={cancelPress}
                onMouseLeave={cancelPress}
                onTouchStart={() => handlePressStart(conv)}
                onTouchEnd={cancelPress}
                onClick={() => openConversation(conv)}
              >
                <div style={styles.avatar}>
                  {isPublic ? "🏛️" : isGroup ? "👥" : initials}
                  {unread && <span style={styles.avatarDot} />}
                </div>

                <div style={styles.content}>
                  <div style={styles.topRow}>
                    <h2 style={styles.name}>{title}</h2>
                    {unread && <div style={styles.badge}>{conv.unread_count}</div>}
                  </div>

                  <p style={styles.message}>
                    {conv.last_message ||
                      (isPublic
                        ? "Salon commun de tous les membres"
                        : "Aucun message pour le moment")}
                  </p>
                </div>

                <div style={styles.chevron}>›</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const getConversationTitle = (conv) => {
  if (conv.type === "public") {
    return conv.name || conv.display_name || "Salon de la loge";
  }

  if (conv.type === "group") {
    return conv.name || conv.display_name || "Groupe";
  }

  if (conv.display_name) return conv.display_name;

  return `${conv.firstname || ""} ${conv.lastname || ""}`.trim() || "Conversation";
};

const getInitials = (conv) => {
  if (conv.type === "public") return "🏛️";
  if (conv.type === "group") return "👥";

  const first = conv.firstname?.charAt(0) || "";
  const last = conv.lastname?.charAt(0) || "";

  return `${first}${last}`.toUpperCase() || "?";
};

const gold = "#d6b56d";

const styles = {
  page: {
    minHeight: "100%",
    padding: "26px 18px 130px",
    background: `
      linear-gradient(rgba(7,16,22,0.85), rgba(3,7,18,0.95)),
      url(${bgTemple})
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    color: "white",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },

  kicker: {
    margin: 0,
    color: gold,
    textTransform: "uppercase",
    letterSpacing: 2,
    fontSize: 12,
    fontWeight: 800,
  },

  title: {
    margin: "6px 0 2px",
    fontSize: 32,
    fontWeight: 900,
  },

  subtitle: {
    margin: 0,
    color: "rgba(255,255,255,0.55)",
    fontSize: 14,
  },

  addBtn: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    border: "none",
    background: "linear-gradient(145deg, #f3d77b, #b9872f)",
    color: "#071016",
    fontSize: 36,
    fontWeight: 900,
    boxShadow: "0 0 30px rgba(214,181,109,0.45)",
  },

  actionsRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 14,
  },

  actionButton: {
    border: "1px solid rgba(214,181,109,0.3)",
    borderRadius: 18,
    padding: "12px 10px",
    background: "rgba(214,181,109,0.1)",
    color: "white",
    fontWeight: 850,
    fontSize: 14,
  },

  infoCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "rgba(214,181,109,0.09)",
    border: "1px solid rgba(214,181,109,0.22)",
    borderRadius: 20,
    padding: 14,
    marginBottom: 18,
  },

  infoIcon: {
    fontSize: 24,
  },

  list: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  card: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: 16,
    borderRadius: 26,
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.09), rgba(255,255,255,0.035))",
    border: "1px solid rgba(214,181,109,0.16)",
    boxShadow: "0 18px 38px rgba(0,0,0,0.32)",
    transition: "0.18s ease",
  },

  publicCard: {
    border: "1px solid rgba(214,181,109,0.55)",
    background:
      "linear-gradient(145deg, rgba(214,181,109,0.16), rgba(255,255,255,0.04))",
  },

  cardPressed: {
    transform: "scale(0.985)",
    background:
      "linear-gradient(145deg, rgba(214,181,109,0.13), rgba(255,255,255,0.04))",
  },

  cardUnread: {
    border: "1px solid rgba(214,181,109,0.45)",
    boxShadow: "0 0 28px rgba(214,181,109,0.16)",
  },

  avatar: {
    position: "relative",
    width: 58,
    height: 58,
    flexShrink: 0,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at top, rgba(214,181,109,0.52), rgba(214,181,109,0.09))",
    border: "1px solid rgba(214,181,109,0.55)",
    color: gold,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 18,
    boxShadow: "0 0 22px rgba(214,181,109,0.22)",
  },

  avatarDot: {
    position: "absolute",
    top: 2,
    right: 3,
    width: 12,
    height: 12,
    borderRadius: "50%",
    background: "#f3d77b",
    boxShadow: "0 0 12px rgba(243,215,123,0.75)",
  },

  content: {
    flex: 1,
    minWidth: 0,
  },

  topRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  name: {
    margin: 0,
    fontSize: 19,
    fontWeight: 850,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  message: {
    margin: "5px 0 0",
    fontSize: 15,
    color: "rgba(255,255,255,0.58)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  badge: {
    minWidth: 26,
    height: 26,
    borderRadius: 999,
    background: "linear-gradient(145deg, #f3d77b, #b9872f)",
    color: "#071016",
    fontSize: 13,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 18px rgba(214,181,109,0.35)",
    flexShrink: 0,
  },

  chevron: {
    color: gold,
    fontSize: 28,
    opacity: 0.75,
  },

  emptyCard: {
    marginTop: 40,
    background:
      "linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
    border: "1px solid rgba(214,181,109,0.2)",
    borderRadius: 28,
    padding: 24,
    textAlign: "center",
    boxShadow: "0 18px 38px rgba(0,0,0,0.3)",
  },

  emptyIcon: {
    fontSize: 42,
    marginBottom: 10,
  },

  emptyButton: {
    marginTop: 14,
    border: "none",
    borderRadius: 16,
    padding: "13px 18px",
    background: "linear-gradient(145deg, #f3d77b, #b9872f)",
    color: "#071016",
    fontWeight: 900,
  },
};

export default ConversationsList;